import React from 'react';
import PropTypes from 'prop-types';
import { strings } from '../../../../localization';
import EventMediaPreloaderUnderlay from './EventMediaPreloaderUnderlay';
import EventMediaImage from './EventMediaImage';
import EventMediaVideo from './EventMediaVideo';
import EventMediaControl from './EventMediaControl';
import EventMediaMessageOverlay from './EventMediaMessageOverlay';
import LiveStream from 'APP_ROOT/components/pages/dashboard/LiveStream/LiveStream';
import imageCamToggle from './images/cam-toggle-w64.svg';
import imagePlay from './images/play-w64.svg';
import imageFullscreen from './images/fullscreen-w64.svg';
import imageDownload from './images/download-w64.svg';
import imageDownloadPending from './images/download-pending-w64.svg';
import imageLiveStream from './images/live-stream-w64.svg';
import imageEventMediaPreviewPlaceholder from './images/preview-placeholder-w480h270.svg';
import {EVENT_MEDIA_STATUS, EVENT_MEDIA_REQUEST_TYPE, EVENT_MEDIA_CAMERA_TYPE} from './eventMediaConstants';
import errorReporting from 'APP_ROOT/services/errorReporting';
import Protected from 'APP_ROOT/components/common/protected';

const PREVIEW_VIDEO_ELEMENT_ID = "PREVIEW_VIDEO_ELEMENT_ID";

export const requestEventMediaFullscreen = (elementRef) => {
    if (!elementRef.current) {
        console.error('EventMedia fullscreen element ref is null');
        return;
    }
    
    const elem = elementRef.current;
    let requestFullscreenPromise;
    
    if (elem.requestFullscreen) {
        if (document.fullscreenElement) {
            requestFullscreenPromise = document.exitFullscreen();
        } else {
            requestFullscreenPromise = elem.requestFullscreen();
        }
    } else if (elem.mozRequestFullScreen) {
        requestFullscreenPromise = elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
        requestFullscreenPromise = elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { 
        requestFullscreenPromise = elem.msRequestFullscreen();
    } else {
        requestFullscreenPromise = Promise.reject('browser does not have a known *RequestFullscreen function');
    }

    requestFullscreenPromise.then(function(value) {
        //console.log("Good");
    }).catch((error) => {
        console.error("EventMedia requestEventMediaFullscreen error", error);
    });
}

export default class EventMedia extends React.PureComponent {

    rav1049RefreshTimestampTimeoutId = null;
    fullscreenElementRef = React.createRef();

    constructor(props) {
        super(props);

        this.state = {
            previewVideoLoaded: false,
            rav1049RefreshTimestamp: null,
            liveStream: false
        }
    }

    componentWillUnmount() {
        if (this.props.previewVideoMediaId) {
            this.props.clearPlaybackError(this.props.previewVideoMediaId);
        }

        if (this.rav1049RefreshTimestampTimeoutId) clearTimeout(this.rav1049RefreshTimestampTimeoutId);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.previewVideoMediaId && (prevProps.previewVideoMediaId !== this.props.previewVideoMediaId)) {
            this.props.clearPlaybackError(prevProps.previewVideoMediaId);
        }
    }

    saveVideo = (e) => {
        /* not used, url is passed to control instead */
        console.log("EventMedia saveVideo for previewVideoUrl: " + this.props.previewVideoUrl);
    }

    toggleCamera = (e) => {
        console.log("EventMedia toggleCamera for current camera type: " + this.props.cameraType);
        this.props.toggleCamera(this.props.cameraType);
    }

    onLiveStream = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        if (!this.props.onLiveStream) {
            console.log("EventMedia this.props.onLiveStream is not a function.");
            return;
        }
        return this.props.onLiveStream(this.props.cameraType, this.props.date);
    }

    onPreviewVideoError = (event) => {

        console.error("EventMedia videoErrorOccurred", this.props.previewVideoUrl, event.target.error);
        errorReporting.error(event.target.error);

        /* It doesn't seem necessary to set state property previewVideoLoaded.
           It shouldn't have been set to true during this attempt.
        this.setState({
            previewVideoLoaded: false
        });*/

        this.props.onVideoError(this.props.previewVideoMediaId, event.target.error);
    }
    
    onClickRequestCreateControl = () => {
        this.props.createEventUserVideo(this.props.eventId, this.props.cameraType, this.props.sourceMediaId);
    }

    retryPlayback = () => {
        this.props.clearPlaybackError(this.props.previewVideoMediaId);
    }

    retry = (status) => {
        return status === EVENT_MEDIA_STATUS.ERROR_PLAYBACK_FAILED ? this.retryPlayback() : this.onClickRequestCreateControl();
    }

    videoCanPlay = (e) => {
        this.setState({
            previewVideoLoaded: true
        });
    }

    pauseVideoPlayback = (e) => {
        var elem = document.getElementById(PREVIEW_VIDEO_ELEMENT_ID);
        if (elem.paused) {
            elem.play();
        } else {
            elem.pause();
        }
    }

    render() {
        var cameraToggleControlShouldRender = true;
        var streamControlShouldRender;
        var fullscreenControlShouldRender;
        var requestCreateControlShouldRender; /* this actually triggers a create preview request. IMPORTANT: video autoplays if available, so play button is only for download request */
        var saveControlShouldRender; /* this triggers 'save as' */

        var status = this.props.status;

        switch (status) {

            case EVENT_MEDIA_STATUS.CREATE_SIGNED_URL_IN_PROGRESS:
            case EVENT_MEDIA_STATUS.SOURCE_GONE:

            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = false;
            streamControlShouldRender = false;
            break;

            case EVENT_MEDIA_STATUS.SOURCE_AVAILABLE:
            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = true;
            saveControlShouldRender = false;
            streamControlShouldRender = true;
            break;

            case EVENT_MEDIA_STATUS.CREATE_PREVIEW_VIDEO_REQUEST_SENT:
            case EVENT_MEDIA_STATUS.MEDIA_INITIATED:
            case EVENT_MEDIA_STATUS.MEDIA_CREATED:
            case EVENT_MEDIA_STATUS.MEDIA_UPLOAD_INITIATED:
            case EVENT_MEDIA_STATUS.MEDIA_UPLOAD_COMPLETED:
            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = false;
            streamControlShouldRender = true;
            break;

            case EVENT_MEDIA_STATUS.PREVIEW_VIDEO_AVAILABLE:
            fullscreenControlShouldRender = true;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = true;
            streamControlShouldRender = true; // TODO small bug potential (need status PREVIEW_VIDEO_AVAILABLE_WITHOUT_SOURCE or something)
            break;

            case EVENT_MEDIA_STATUS.ERROR: // DEPRECATED_VIDEO_PREVIEW_REQUEST
            case EVENT_MEDIA_STATUS.ERROR_REQUEST_TIMEOUT:
            case EVENT_MEDIA_STATUS.ERROR_MQTT_FAILED:
            case EVENT_MEDIA_STATUS.ERROR_UPLOAD_FAILED:
            case EVENT_MEDIA_STATUS.ERROR_SHORT_FAILED:
            case EVENT_MEDIA_STATUS.ERROR_RECORDING_FAIL:
            case EVENT_MEDIA_STATUS.ERROR_DOWNLOAD_FAILED:
            case EVENT_MEDIA_STATUS.ERROR_MEDIA_FAILED:

            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = false;
            streamControlShouldRender = true; // TODO small bug potential (need status PREVIEW_VIDEO_AVAILABLE_WITHOUT_SOURCE or something)
            break;

            case EVENT_MEDIA_STATUS.ERROR_PLAYBACK_FAILED:
            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = true;
            streamControlShouldRender = false;
            break;

            case EVENT_MEDIA_STATUS.NOT_FOUND_PREVIEW_IMAGE:
            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = false;
            streamControlShouldRender = false;
            break;

            case EVENT_MEDIA_STATUS.SOURCE_AVAILABLE_WITHOUT_PREVIEW_IMAGE:
            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = true;
            saveControlShouldRender = false;
            streamControlShouldRender = true;
            break;
            default:
            break;
        }

        /* regardless of the status, also check the urls (since they are optional) */
        if (fullscreenControlShouldRender === false) {

            if (this.props.previewVideoUrl && status !== EVENT_MEDIA_STATUS.ERROR_PLAYBACK_FAILED) {

                fullscreenControlShouldRender = true;

                if (status !== EVENT_MEDIA_STATUS.PREVIEW_VIDEO_AVAILABLE) {
                    console.warn("EventMedia render for status " + status + " while props included a previewVideoUrl.", this.props);
                    console.warn("EventMedia render for status " + status + " overriding status to PREVIEW_VIDEO_AVAILABLE.");

                    status = EVENT_MEDIA_STATUS.PREVIEW_VIDEO_AVAILABLE;

                    requestCreateControlShouldRender = false;
                    saveControlShouldRender = true;
                }
            } 
        } else if (!this.props.previewVideoUrl) { /* fullscreenControlShouldRender is true */

            console.warn("EventMedia render for status " + status + " but previewVideoUrl is invalid.", this.props);
            console.warn("EventMedia render for status " + status + " overriding status to SOURCE_AVAILABLE.");

            status = EVENT_MEDIA_STATUS.SOURCE_AVAILABLE;

            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = true;
            saveControlShouldRender = false;
        }

        if (!this.props.mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.NONE) {
            fullscreenControlShouldRender = false;
            requestCreateControlShouldRender = false;
            saveControlShouldRender = false;
        }

        /* EventMediaVideo callbacks can update state on video load attempts, check those states: */
        if (this.state.previewVideoLoaded === false) {
            saveControlShouldRender = false;
            fullscreenControlShouldRender = false;
        }

        /* Don't show camera switch icon if the camera type is not compatible with road/cabin switching (e.g. "CAMERA_UNKNOWN") */
        if (this.props.previewVideoUrl &&
            this.props.previewVideoCameraType !== EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN &&
            this.props.previewVideoCameraType !== EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD) {
            cameraToggleControlShouldRender = false;
        }

        if (!this.props.toggleCamera) { // if no toggle camera function prop
            cameraToggleControlShouldRender = false;
        }

        let cameraTypeForOverlayMessages = this.props.cameraType;
        if (this.props.shouldShowAlternateCameraType) {
            if (cameraTypeForOverlayMessages === EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD) {
                cameraTypeForOverlayMessages = EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN;
            } else {
                cameraTypeForOverlayMessages = EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD;
            }
        }

        const fullscreenControl = (
            <div className="fullscreen-control">
            <EventMediaControl
                id="EVENT_MEDIA_CONTROL_FULLSCREEN"
                iconSrc={imageFullscreen}
                render={true}
                onClick={() => {requestEventMediaFullscreen(this.fullscreenElementRef)}}
            />
            </div>
        );

        if (this.props.eventType === "EVENT_KLOUD_HISTORICAL_STREAMING" // RAV-1399
            || this.props.eventType === "EVENT_KLOUD_LIVE_STREAMING") { 
            streamControlShouldRender = false;
        }
        if (!this.props.historicalStreamingEnabled) { // ie if FALSE
            streamControlShouldRender = false; // reminder: LiveMedia handles live streaming
        }

       const streamControl = (
            <Protected
                resource='ravens.video_stream'
                action="view"
                resourceId={this.props.selectedRavenUuid}
            >
                <EventMediaControl
                    id="EVENT_MEDIA_CONTROL_STREAM"
                    iconSrc={imageLiveStream}
                    render={streamControlShouldRender}
                    onClick={this.onLiveStream}
                    disabled={this.props.offline && strings.event_streaming_request_error_raven_offline}
                />
            </Protected>
        );

        const cameraToggleControl = (
            <EventMediaControl
                id="EVENT_MEDIA_CONTROL_CAMERA_TOGGLE"
                iconSrc={imageCamToggle}
                render={cameraToggleControlShouldRender}
                onClick={this.toggleCamera}
            />
        );
        // RAV-1049 - events that are <60 seconds old probably do not have video yet
        // IMPORTANT - there is a corresponding RAV-1049 patch in dashboard/eventScrollableList.js
        let millisecondsAgo = Date.now() - this.props.date;;
        let rav1049RefreshTimestampTimeout = 60000; // 60 seconds
        if (millisecondsAgo < 0) {
            rav1049RefreshTimestampTimeout += Math.abs(millisecondsAgo);
            millisecondsAgo = 0; // consider this a current event
        }
        if (Math.round(millisecondsAgo/1000) <= 60) {
            rav1049RefreshTimestampTimeout -= millisecondsAgo - 1000; // 60+1 seconds from the event's timestamp (adding 1 second to ensure above condition passes)
            if (this.rav1049RefreshTimestampTimeoutId) clearTimeout(this.rav1049RefreshTimestampTimeoutId);
            this.rav1049RefreshTimestampTimeoutId = setTimeout(() => {
                this.setState({
                    rav1049RefreshTimestamp: Math.round(new Date().getTime()/1000)
                });
            }, rav1049RefreshTimestampTimeout);
            requestCreateControlShouldRender = false;
        }
        
        let requestCreateControl = null;
        if (this.props.createEventUserVideo) (
            requestCreateControl = <EventMediaControl
                id="EVENT_MEDIA_CONTROL_REQUEST_CREATE"
                iconSrc={imagePlay}
                render={requestCreateControlShouldRender}
                onClick={this.onClickRequestCreateControl}
                disabled={(this.props.unplugged || this.props.offline) && strings.media_request_error_raven_offline}
                pending={this.props.offline === undefined}
            />
        );

        const saveControl = (
            <EventMediaControl
                id="EVENT_MEDIA_CONTROL_SAVE"
                iconSrc={imageDownload}
                render={saveControlShouldRender}
                onClick={this.props.previewVideoUrl} /* sending a link/url, EventMediaControl will use an anchor for the onClick handler */
            />
        );

        return (
            <div className="col-12 image-panel panel-wrapper">
                <div className="row section-wrapper event-image">
                    <div className="col-12 section-body">
                        <div className="event-media-max-width-480">
                            <div className="event-media-wrapper-16-9-aspect-ratio" ref={this.fullscreenElementRef}>
                                <div className="event-media-wrapper">
                                    <EventMediaPreloaderUnderlay
                                        status={status}
                                    />
                                    <EventMediaImage
                                        previewImageUrl={this.props.previewImageUrl ? this.props.previewImageUrl : imageEventMediaPreviewPlaceholder }
                                        date = {this.props.date}
                                    />
                                    <EventMediaVideo
                                        previewVideoUrl={this.props.previewVideoUrl}
                                        id={PREVIEW_VIDEO_ELEMENT_ID}
                                        onClick={this.pauseVideoPlayback}
                                        onError={this.onPreviewVideoError}
                                        onCanPlay={this.videoCanPlay}
                                        autoPlay={this.props.autoPlay}
                                        status={status}
                                    />
                                    <EventMediaMessageOverlay
                                        cameraType={cameraTypeForOverlayMessages}
                                        status={status}
                                        retry={() => this.retry(status)}
                                    />
                                    <div className="event-media-controls">
                                        {streamControl}
                                        {requestCreateControl}
                                        {saveControl}
                                        {fullscreenControl}
                                        {cameraToggleControl}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

EventMedia.propTypes = {
    shouldShowAlternateCameraType: PropTypes.bool,
    cameraType: PropTypes.string.isRequired,
    sourceMediaId: PropTypes.string, /* optional, if available */
    eventId: PropTypes.string.isRequired,
    eventType: PropTypes.string, /* optional, might be null */
    previewVideoMediaId: PropTypes.string, /* optional, if available */
    previewVideoCameraType: PropTypes.string, /* optional, if available */
    previewImageUrl: PropTypes.string, /* optional, will be null on init */
    mediaRequestType: PropTypes.string.isRequired,
    previewVideoUrl: PropTypes.string, /* optional, if available, will be null on init */
    date: PropTypes.instanceOf(Date),
    status: PropTypes.string.isRequired,
    createEventUserVideo: PropTypes.func, /* optional, if not provided then the download/play control is hidden */
    toggleCamera: PropTypes.func, /* optional, if not provided then the toggle camera control is hidden regardless of media type */
    onVideoError: PropTypes.func.isRequired,
    clearPlaybackError: PropTypes.func, /* optional, called in componentDidUpdate when previewVideoMediaId changes */
    onLiveStream: PropTypes.func, /* optional, called when the live stream control is clicked */
    autoPlay: PropTypes.bool, /* defaults to true, used by cluster component to avoid loading all videos in the cluster */
    offline: PropTypes.bool, /* optional, disables stream/request controls when raven is offline */
    unplugged: PropTypes.bool, /* optional, disables request create control when raven is unplugged */
    historicalStreamingEnabled: PropTypes.bool, /* optional, hides stream control when false */
    selectedRavenUuid: PropTypes.string, /* optional, used as resourceId for the Protected stream control */
};
